/**
 * La campana del encabezado.
 *
 * POR QUÉ SE REESCRIBIÓ. Era un `ActionList` de Polaris — el componente de los menús contextuales,
 * pensado para "Editar / Duplicar / Eliminar". Una notificación no es una acción de menú: tiene
 * ícono, dos líneas de texto, hora y estado de leído. Metida en un ActionList quedaba con el título
 * en negrita, el cuerpo en letra chica de ayuda y una etiqueta "Nuevo" que empujaba todo — apretado
 * y sin jerarquía. Ahora usa el MISMO marco que el panel de chats, así los dos desplegables del
 * encabezado se abren igual, miden igual y scrollean igual.
 *
 * 🪤 LA HORA VIENE DEL MISMO HELPER QUE LOS CHATS. Que una lista diga "2 h" y la de al lado "hace 2
 * horas" es de esas incoherencias que hacen sentir la app cosida a pedazos.
 */

import { useMemo, useState } from 'react';
import { Icon, Text } from '@/lib/polaris';
import { NotificationIcon } from '@/lib/polaris-icons';
import { InsigniaDelAviso } from '@/components/layout/InsigniaDelAviso';
import { useNotifications, type AppNotification } from '@/hooks/useNotifications';
import { useAbrirAviso } from '@/hooks/useAbrirAviso';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { ROUTES } from '@/config/routes.config';
import { waTimeAgo } from '@/components/whatsapp/wa-helpers';
import { PanelDelEncabezado, FilaDelPanel } from '@/components/layout/PanelDelEncabezado';

export function NotificacionesMenu() {
  const [abierto, setAbierto] = useState(false);
  const [pestania, setPestania] = useState<'todas' | 'sin_leer'>('todas');
  const abrirAviso = useAbrirAviso();
  const navigate = useStoreNavigate();
  const { notifications, unreadCount, loading, markRead, markAllRead } = useNotifications();

  const visibles = useMemo(
    () => (pestania === 'sin_leer' ? notifications.filter((n) => !n.read_at) : notifications),
    [notifications, pestania],
  );

  /*
   * 🚨 ANTES NAVEGABA A `n.url` TAL CUAL, y casi todas eran `/m/orders/<id>`: una interfaz retirada
   *    que manda al Inicio. Un drop: «le doy clic y no hace nada». El destino lo decide el hecho que
   *    avisa (el pedido, el chat), no la foto de la ruta guardada. Ver `aDondeLlevaElAviso`.
   */
  const abrir = (n: AppNotification) => {
    setAbierto(false);
    void markRead(n.id);
    abrirAviso(n);
  };

  const activador = useMemo(() => (
    <button
      type="button"
      onClick={() => setAbierto((v) => !v)}
      aria-label={unreadCount > 0 ? `Notificaciones (${unreadCount} sin leer)` : 'Notificaciones'}
      // El fondo gris del encabezado sale de `aria-expanded` — ver la nota en WaChatsMenu: antes lo
      // tenía siempre el ícono de wifi y nunca este.
      aria-expanded={abierto}
      aria-haspopup="menu"
      className="sf-topbar-icon-button"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', width: 36, height: 36,
        background: 'transparent', border: 'none', borderRadius: 8,
        cursor: 'pointer', color: 'inherit',
      }}
    >
      <Icon source={NotificationIcon} tone="base" />
      {unreadCount > 0 && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute', top: 1, right: 1, minWidth: 15, height: 15,
            padding: '0 3px', borderRadius: 9, background: 'var(--p-color-bg-fill-critical)',
            color: '#fff', fontSize: 9, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
          }}
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  // 🪤 `abierto` VA EN LAS DEPENDENCIAS O EL FONDO NUNCA APARECE. El activador está memoizado; sin
  // esta dependencia el `aria-expanded` queda congelado en el valor del primer render y la regla de
  // CSS que pinta el ícono abierto no se activa jamás. El lint lo marcó como aviso, pero acá el
  // efecto es funcional: la corrección visual entera no se vería.
  ), [unreadCount, abierto]);

  return (
    <PanelDelEncabezado
      abierto={abierto}
      onCerrar={() => setAbierto(false)}
      activador={activador}
      titulo="Notificaciones"
      cargando={loading}
      pestanias={[
        { id: 'todas', texto: 'Todas' },
        { id: 'sin_leer', texto: 'No leídas', contador: unreadCount },
      ]}
      pestaniaActiva={pestania}
      onPestania={(id) => setPestania(id as typeof pestania)}
      // La acción aparece solo si hay algo que marcar: un botón que no hace nada enseña a ignorarlo.
      accion={unreadCount > 0 ? { texto: 'Marcar todas como leídas', onClick: () => void markAllRead() } : undefined}
      // La campana trae las últimas 20; el resto vive en su página (founder, 20-oct: «muestra hasta cierto punto»).
      pie={{ texto: 'Ver todas las notificaciones', onClick: () => { setAbierto(false); navigate(ROUTES.NOTIFICACIONES); } }}
      vacio={(
        <Text as="p" tone="subdued">
          {pestania === 'sin_leer'
            ? 'No hay notificaciones sin leer. Todo al día.'
            : 'No tienes notificaciones. Acá van a aparecer los avisos de pedidos, pagos e integraciones.'}
        </Text>
      )}
    >
      {visibles.map((n) => (
        <FilaDelPanel
          key={n.id}
          onClick={() => abrir(n)}
          avatar={<InsigniaDelAviso tipo={n.type} />}
          titulo={n.title}
          detalle={n.body}
          hora={waTimeAgo(n.created_at)}
          sinLeer={!n.read_at}
        />
      ))}
    </PanelDelEncabezado>
  );
}
